// src/AddLanguageModal.tsx
// NOTE: Update frontend/src/__tests__/AddLanguageModal.test.tsx and run
// `../scripts/test-frontend.sh AddLanguageModal` when editing this modal.
import React, { useState } from 'react'
import { Modal, Select, Button, message, Spin, Tag } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import axios from 'axios'
import { getAuthHeaders } from './utils/authHeaders'
import { resolveFrontendApiBase } from './utils/apiBase'
import { isLanguageBeta } from './LanguageConfig'
import './AddLanguageModal.css'

const API_BASE = resolveFrontendApiBase()

interface AddLanguageModalProps {
  isVisible: boolean
  onClose: () => void
  onLanguageAdded: () => void
  bookId: string
  existingLanguages: string[]
  allLanguages: string[]
  sourceLanguage: string
  betaLanguages: string[]
}

const AddLanguageModal: React.FC<AddLanguageModalProps> = ({
  isVisible,
  onClose,
  onLanguageAdded,
  bookId,
  existingLanguages,
  allLanguages,
  sourceLanguage,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  /** Small “BETA” badge */
  const BetaTag = (): JSX.Element => (
    <Tag color="orange" className="add-language-modal__beta-tag">
      BETA
    </Tag>
  )

  /** POST selected language to the backend */
  const handleAddLanguage = async (): Promise<void> => {
    if (!selectedLanguage) {
      message.error('Please select a language')
      return
    }

    setIsLoading(true)
    try {
      await axios.post(
        `${API_BASE}/add-language/${bookId}`,
        JSON.stringify({ language: selectedLanguage }),
        { headers: { 'Content-Type': 'application/json', ...await getAuthHeaders() } },
      )
      message.success('Language added successfully')
      onLanguageAdded()
      onClose()
    } catch (error) {
      /* eslint-disable no-console */
      console.error('Error adding language:', error)
      /* eslint-enable no-console */
      message.error('Failed to add language')
    } finally {
      setIsLoading(false)
    }
  }

  const availableLanguages = allLanguages.filter(
    (lang) => !existingLanguages.includes(lang) && lang !== sourceLanguage,
  )
  const hasAvailableLanguages = availableLanguages.length > 0
  const canAddLanguage = Boolean(selectedLanguage) && hasAvailableLanguages && !isLoading
  const readinessCopy = selectedLanguage
    ? `${selectedLanguage} is ready to add.`
    : hasAvailableLanguages
      ? 'Select a target language to continue.'
      : 'All available target languages are already on this book.'

  return (
    <Modal
      title="Add Language"
      visible={isVisible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="add"
          type="primary"
          disabled={!canAddLanguage}
          loading={isLoading}
          onClick={handleAddLanguage}
        >
          Add
        </Button>,
      ]}
    >
      <Spin spinning={isLoading}>
        <div className="add-language-modal">
          <div className="add-language-modal__block">
            <div className="add-language-modal__eyebrow">Target language</div>
            <p className="add-language-modal__helper">
              Choose a language that is not already attached to this book. The source language,
              {sourceLanguage ? ` ${sourceLanguage},` : ''} is excluded from this list.
            </p>
            <Select
              className="add-language-modal__select"
              placeholder="Select a language"
              value={selectedLanguage || undefined}
              onChange={setSelectedLanguage}
              disabled={!hasAvailableLanguages}
              suffixIcon={
                <span className="add-language-modal__select-icon" aria-hidden="true">
                  <DownOutlined />
                </span>
              }
            >
              {availableLanguages.map((lang) => (
                <Select.Option key={lang} value={lang}>
                  <span className="add-language-modal__option">
                    {lang}
                    {isLanguageBeta(lang) && <BetaTag />}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </div>
          <div
            className={`add-language-modal__readiness ${
              canAddLanguage ? 'add-language-modal__readiness--ready' : ''
            }`}
            aria-live="polite"
          >
            {readinessCopy}
          </div>
        </div>
      </Spin>
    </Modal>
  )
}

export default AddLanguageModal
